/**
 * 全局导航顶栏进度（Polymarket / NProgress 式感知进度）。
 * 非真实接口百分比：start 假爬 → critical 就绪 done；silent 换盘可 skip。
 */

import { computed, readonly, ref } from 'vue';

const progress = ref(0);
const visible = ref(false);

let generation = 0;
let trickleTimer = null;
let safetyTimer = null;
let fadeTimer = null;
let waitingCritical = false;
let skipNextCount = 0;

const TRICKLE_MS = 320;
const SAFETY_MS = 12000;
/** 与 NavigationProgressBar transform 过渡对齐：先冲到 100% 再淡出 */
const DONE_FILL_MS = 320;
const DONE_FADE_MS = 200;
const CAP = 0.94;

function clearTrickle() {
  if (trickleTimer != null) {
    clearInterval(trickleTimer);
    trickleTimer = null;
  }
}

function clearSafety() {
  if (safetyTimer != null) {
    clearTimeout(safetyTimer);
    safetyTimer = null;
  }
}

function clearFade() {
  if (fadeTimer != null) {
    clearTimeout(fadeTimer);
    fadeTimer = null;
  }
}

function clampProgress(value) {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function scheduleSafety(gen) {
  clearSafety();
  safetyTimer = setTimeout(() => {
    if (gen !== generation) return;
    forceDone();
  }, SAFETY_MS);
}

function startTrickle(gen) {
  clearTrickle();
  trickleTimer = setInterval(() => {
    if (gen !== generation || !visible.value) return;
    const current = progress.value;
    if (current >= CAP) return;
    const remain = CAP - current;
    const step = remain * (0.08 + Math.random() * 0.12);
    progress.value = clampProgress(current + Math.max(0.005, step));
  }, TRICKLE_MS);
}

/**
 * 下一次路由导航不展示进度（同页 silent replace：分钟盘切轮、resolve collId 等）。
 */
export function skipNextNavigationProgress() {
  skipNextCount += 1;
}

export function consumeNavigationProgressSkip() {
  if (skipNextCount <= 0) return false;
  skipNextCount -= 1;
  return true;
}

export function startNavigationProgress() {
  clearFade();
  clearTrickle();
  clearSafety();
  waitingCritical = false;
  generation += 1;
  const gen = generation;
  visible.value = true;
  progress.value = 0.12;
  requestAnimationFrame(() => {
    if (gen !== generation) return;
    progress.value = 0.42;
  });
  startTrickle(gen);
  scheduleSafety(gen);
}

/** afterEach：普通页直接收尾；详情等 critical 页继续假爬直到 complete */
export function finishOrHoldNavigationProgress(holdCritical) {
  if (!visible.value && progress.value <= 0) return;
  if (holdCritical) {
    waitingCritical = true;
    scheduleSafety(generation);
    return;
  }
  forceDone();
}

/** 详情关键数据就绪（成功或失败均可） */
export function completeNavigationProgressCritical() {
  if (!waitingCritical && !visible.value) return;
  waitingCritical = false;
  forceDone();
}

function forceDone() {
  const gen = generation;
  clearTrickle();
  clearSafety();
  waitingCritical = false;
  // 先画到 100%，等 transform 过渡完成后再淡出，避免「停在中间就消失」
  progress.value = 1;
  clearFade();
  fadeTimer = setTimeout(() => {
    if (gen !== generation) return;
    visible.value = false;
    fadeTimer = setTimeout(() => {
      if (gen !== generation) return;
      progress.value = 0;
      fadeTimer = null;
    }, DONE_FADE_MS);
  }, DONE_FILL_MS);
}

export function useNavigationProgress() {
  return {
    progress: readonly(progress),
    visible: readonly(visible),
    percent: computed(() => Math.round(clampProgress(progress.value) * 100)),
  };
}
